import React, { useMemo, useState, useCallback, useEffect } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  FormControl,
  Divider,
  Drawer,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ColDef } from "ag-grid-community";
import AgGridTable from "../../../components/table/AgGridTable";
import WhiteTooltip from "../../../components/WhiteTooltip";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import NotApplicableImg from '../../../assets/baseVersion/NotApplicable.svg';

const MISCONFIG_STATUS_META = {
  passed: {
    icon: DoneIcon,
    label: "Passed",
    color: "#00A61B",
    lightBg: "#F0FFF4",
    darkBg: "#0E1C11",
    iconBg: "rgba(0, 166, 27, 0.15)",
  },
  failed: {
    icon: CloseIcon,
    label: "Failed",
    color: "#F03C3C",
    lightBg: "#FFF5F5",
    darkBg: "#1F0E0E",
    iconBg: "rgba(240, 60, 60, 0.15)",
  },
} as const;

interface ViewSummaryDrawerProps {
  open: boolean;
  onClose: () => void;
  summaryData: any;
  isLoading?: boolean;
  vendor: any;
}

const valueStyles = {
  fontSize: "clamp(14px, 1.6vw, 18px)",
  fontWeight: 600,
};

const rowStyle = {
  display: "flex",
  alignItems: "center",
};

const labelStyle = {
  fontWeight: 700,
  fontSize: "13px",
  width: 160,        // 🔥 fixed width = perfect alignment
  flexShrink: 0,
};

const normalizeNumber = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return value;
  return Number(value.replace("+", "")) || 0;
};

interface StatValueProps {
  value: string | number;
  reverseColors?: boolean;
  reverseArrows?: boolean;
}

const StatValue: React.FC<StatValueProps> = ({ value, reverseColors = false, reverseArrows = false }) => {
  const numericValue = normalizeNumber(value);

  if (numericValue > 0) {
    const color = reverseColors ? "#F03C3C" : "#00A61B";
    const Icon = reverseArrows ? CallReceivedIcon : ArrowOutwardIcon;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Icon sx={{ fontSize: 16, color }} />
        <Typography sx={valueStyles}>{numericValue}</Typography>
      </Box>
    );
  }

  if (numericValue < 0) {
    const color = reverseColors ? "#00A61B" : "#F03C3C";
    const Icon = reverseArrows ? ArrowOutwardIcon : CallReceivedIcon;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Icon sx={{ fontSize: 16, color }} />
        <Typography sx={valueStyles}>{Math.abs(numericValue)}</Typography>
      </Box>
    );
  }

  // ZERO → no arrow
  return <Typography sx={valueStyles}>{numericValue}</Typography>;
};

type RawStatus = "PASSED" | "FAILED" | null;

type MisconfigItem = {
  id: string;
  name: string;
  previous_status: RawStatus;
  current_status: RawStatus;
};

type NormalizedMisconfig = {
  id: string;
  name: string;
  status: "passed" | "failed";
  showRedDot?: boolean;
  showGreenDot?: boolean;
  showGrayDot?: boolean;
  showNotApplicable?: boolean;
  sortOrder: number;
};

interface InlineStatBlockProps {
  label: string;
  value: string | number;
  reverseColors?: boolean;
  reverseArrows?: boolean;
}

const ViewSummaryDrawer: React.FC<ViewSummaryDrawerProps> = ({
  open,
  onClose,
  summaryData,
  isLoading = false,
  vendor,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [misconfigFilter, setMisconfigFilter] = useState<"all" | "changes">("changes");
  const handleFilterInteraction = useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation();
  }, []);

  useEffect(() => {
    if (open) {
      console.log("is_first_scan =>", summaryData?.is_first_scan);

      if (summaryData.is_first_scan) {
        setMisconfigFilter("all");
      } else {
        setMisconfigFilter("changes");
      }
    }
  }, [open, summaryData]);

  const InlineStatBlock: React.FC<InlineStatBlockProps> = ({ label, value, reverseColors, reverseArrows }) => {
    return (
      <Box
        sx={{
          textAlign: "center",
          minWidth: 90,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          lineHeight: 1.2,
        }}
      >
        <StatValue value={value} reverseColors={reverseColors} reverseArrows={reverseArrows} />
        <Typography
          sx={{
            fontSize: 11,
            color: "text.secondary",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Typography>
      </Box>
    );
  };

  const cveColumnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: "Name",
        field: "cve_id",
        sortable: false,
        minWidth: 140,
        maxWidth: 180,
        filter: false,
        cellRenderer: (params: any) => (
          <WhiteTooltip title={params.value} arrow>
            <span className="ag-ellipsis">
              {params.value ? params.value : '-'}
            </span>
          </WhiteTooltip>
        ),
      },
      {
        headerName: "Severity",
        field: "severity",
        minWidth: 120,
        maxWidth: 150,
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => {
          const severityStyles: any = {
            Critical: { bgcolor: isDarkMode ? "rgba(255, 61, 61, 0.18)" : "#67000424", color: isDarkMode ? "#FF3D3D" : "#670004" },
    High: { bgcolor: isDarkMode ? "rgba(255, 107, 107, 0.15)" : "#A7000F24", color: isDarkMode ? "#FF6B6B" : "#A7000F" },
    Medium: { bgcolor: isDarkMode ? "rgba(255, 167, 38, 0.15)" : "#E8580024", color: isDarkMode ? "#FFA726" : "#E85800" },
    Low: { bgcolor: isDarkMode ? "rgba(255, 224, 130, 0.15)" : "#F2BF0024", color: isDarkMode ? "#FFE082" : "#F2BF00" },
  };
          return (
            <Chip
              label={params.value}
              size="small"
              sx={{
                bgcolor: severityStyles[params.value]?.bgcolor || "#f5f5f5",
                color: severityStyles[params.value]?.color || "#666666",
                height: "20px",
                borderRadius: "4px",
                position: "relative",
                fontWeight: 600,
                p: 1.5,
                pr: 0,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 8,
                  height: 8,
                  bgcolor: severityStyles[params.value]?.color || "#666666",
                  borderRadius: "50%",
                },
                "& .MuiChip-label": { px: 1 },
              }}
            />
          );
        },
      },
      {
        headerName: "CVSS",
        field: "cvss_score",
        minWidth: 80,
        maxWidth: 100,
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => (
          <WhiteTooltip title={params.value} arrow>
            <span className="ag-ellipsis">
              {params.value ? params.value : '-'}
            </span>
          </WhiteTooltip>
        ),
      },
      {
        headerName: "CISA's KEV",
        field: "cisaKev",
        minWidth: 120,
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => {
          const isExploited = Boolean(params.value);

          return (
            <span
              className="ag-ellipsis"
              style={{
                color: isExploited ? "#D32F2F" : "black", // red if exploited
                fontSize: isExploited ? 12 : 12,
                fontWeight: isExploited ? 500 : 500,
              }}
            >
              {isExploited ? "Exploited" : "Not Exploited"}
            </span>
          );
        },
      },
      {
        headerName: "EPSS",
        field: "epss",
        minWidth: 100,
        maxWidth: 100,
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => {
          const raw = params.value;

          if (!raw) return <span className="ag-ellipsis">-</span>;

          const percentage = (Number(raw) * 100).toFixed(3); // 0.00151 → 0.151%

          return (
            <WhiteTooltip title={`${percentage}%`} arrow>
              <span className="ag-ellipsis">
                {percentage}%
              </span>
            </WhiteTooltip>
          );
        },
      },
      {
        headerName: "",
        field: "last_status",
        minWidth: 60,
        maxWidth: 60,
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => {
          const prev = params.data?.previous_status ?? null;
          const curr = params.data?.current_status ?? null;

          const showPlus =
            (prev === "PRESENT" && curr === null) ||   // PRESENT → null
            (prev === null && curr === null);          // null → null

          const showMinus =
            (prev === null && curr === "PRESENT") ||   // null → PRESENT
            (prev === "PRESENT" && curr === "PRESENT"); // PRESENT → PRESENT

          if (showPlus) {
            // ➕ PLUS ICON (REMEDIATED - GREEN)
            return (
              <WhiteTooltip title="Remediated" arrow>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    bgcolor: "#E8F5E9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AddIcon sx={{ fontSize: 16, color: "#2E7D32" }} />
                </Box>
              </WhiteTooltip>
            );
          }

          if (showMinus) {
            // ➖ MINUS ICON (ADDED - RED)
            return (
              <WhiteTooltip title="Added" arrow>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    bgcolor: "#FDECEA",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <RemoveIcon sx={{ fontSize: 16, color: "#D32F2F" }} />
                </Box>
              </WhiteTooltip>
            );
          }
          // If current is NOT_APPLICABLE show the NA image
          if (curr === "NOT_APPLICABLE") {
            return (
              <WhiteTooltip title="Not applicable" arrow>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    bgcolor: "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box component="img" src={NotApplicableImg} alt="NA" sx={{ width: 18, height: 18 }} />
                </Box>
              </WhiteTooltip>
            );
          }
          // If previous status was NOT_APPLICABLE, show a gray-dot overlay
          if (prev === "NOT_APPLICABLE") {
            return (
              <WhiteTooltip title="" arrow>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    bgcolor: isDarkMode ? "#2D2D2D" : "#F3F4F6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  {/* show a fail icon when current is FAILED */}
                  {curr === "FAILED" && (
                    <CancelIcon sx={{ fontSize: 16, color: "#F03C3C" }} />
                  )}

                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: isDarkMode ? "#9CA3AF" : "#9EA7AD",
                      border: `1px solid ${theme.palette.background.paper}`,
                    }}
                  />
                </Box>
              </WhiteTooltip>
            );
          }

          return <span>-</span>;
        },
      },
    ],
    [isDarkMode]
  );

  const getSortOrder = (prev: RawStatus, curr: RawStatus): number => {
    if (prev === null && curr === "PASSED") return 1;
    if (prev === "PASSED" && curr === null) return 2;
    if (prev === "FAILED" && curr === "PASSED") return 3;
    if (prev === "PASSED" && curr === "PASSED") return 4;
    if (prev === "FAILED" && curr === "FAILED") return 5;
    if (prev === null && curr === null) return 6;
    return 99;
  };

  const filteredMisconfigurationChanges = useMemo<NormalizedMisconfig[]>(() => {
    const raw: MisconfigItem[] = summaryData?.misconfigurationChanges ?? [];

    // ---------------------------
    // CASE 1: FIRST SCAN
    // ---------------------------
    if (summaryData?.is_first_scan) {
      // If filter = CHANGES → show nothing
      if (misconfigFilter === "changes") return [];

      // If filter = ALL → show everything using only current_status
      return raw.map((item) => {
        const curr = item.current_status;

        const status: "passed" | "failed" =
          curr === "PASSED" ? "passed" : "failed";

        return {
          id: item.id,
          name: item.name,
          status,
          sortOrder: 1,
        };
      });
    }

    // ---------------------------
    // CASE 2: NOT FIRST SCAN
    // ---------------------------
    const normalized = raw.map((item) => {
      const prev = item.previous_status ?? null;
      const curr = item.current_status ?? null;

      const hasChanged = prev !== curr;

      const status: "passed" | "failed" =
        curr === "PASSED" ? "passed" : "failed";

      // mark items where current is NOT_APPLICABLE so UI can show the NA image
      const showNotApplicable = curr === "NOT_APPLICABLE";

      const showGreenDot =
        (prev === "PASSED" && curr !== null) ||
        (prev === null && curr === "PASSED");

      const showRedDot =
        prev === "FAILED" ||
        (prev === "PASSED" && curr === null);

      // Show gray overlay when previous status was NOT_APPLICABLE
      const showGrayDot = prev === "NOT_APPLICABLE";

      return {
        id: item.id,
        name: item.name,
        status,
        showGreenDot,
        showRedDot,
        showGrayDot,
        showNotApplicable,
        hasChanged,
        sortOrder: getSortOrder(prev, curr),
      };
    });

    // ---------------------------
    // FILTER LOGIC
    // ---------------------------
    const filtered =
      misconfigFilter === "changes"
        ? normalized.filter((i) => i.hasChanged) // only real diffs
        : normalized;                            // all rows

    return filtered.sort((a, b) => a.sortOrder - b.sortOrder);
  }, [
    summaryData?.misconfigurationChanges,
    misconfigFilter,
    summaryData?.is_first_scan,
  ]);

  const normalizedVendor = (vendor ?? "").toLowerCase();

  const isLinuxVendor = ["redhat", "ubuntu", "debian", "huawei", "apple"].includes(normalizedVendor);

  const filteredCves = useMemo(() => {
    if (!Array.isArray(summaryData?.cves)) return [];

    return summaryData.cves.filter((cve: any) => {
      const prev = cve.previous_status ?? null;
      const curr = cve.current_status ?? null;

      // ❌ EXCLUDE: no change
      if (
        (prev === "PRESENT" && curr === "PRESENT") ||
        (prev === null && curr === null)
      ) {
        return false;
      }

      // ✅ INCLUDE: actual changes
      return prev !== curr;
    });
  }, [summaryData?.cves]);

  const hasCveChanges = filteredCves.length > 0;

  const shouldShowSkeleton =
    isLoading ||
    !summaryData ||
    !summaryData.configurationChanges ||
    !summaryData.vulnerabilityChanges ||
    !summaryData.cveChanges;

  const configurationChanges = summaryData.is_first_scan
    ? { all_changes: 0, additions: 0, deletions: 0, modified: 0 }
    : summaryData.configurationChanges;

  const vulnerabilityChanges = summaryData.is_first_scan
    ? { total_changes: 0, added: 0, removed: 0 }
    : summaryData.vulnerabilityChanges;

  const cveChanges = summaryData.is_first_scan
    ? { total_count: 0, added: 0, removed: 0 }
    : summaryData.cveChanges;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "38%",
          bgcolor: theme.palette.mode === "dark" ? "#1E1E1E" : "#FFFFFF",
          borderLeft: `1px solid ${theme.palette.mode === "dark" ? "#2D2D2D" : "#E5E7EB"
            }`,
          overflow: "hidden",
          // p: 1.5
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          // mb: 2,
        }}
      >
        <span style={{ fontWeight: 700, fontSize: "16px", padding: '10px' }}>
          Summary
        </span>
        <IconButton size="small" onClick={onClose} sx={{ color: "gray", p: 1 }}>
          <CancelIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Divider />

      <Stack spacing={1} sx={{ flex: 1, overflowY: "auto" }} className="scroll">
        <div
          style={{
            padding: 2,
            backgroundColor: isDarkMode ? "#1E1E1E" : "#ffffff",
            borderRadius: 0,
            boxShadow: "none",
          }}
        >
          {shouldShowSkeleton ? (
            /* =================== FULL DRAWER SKELETON =================== */
            <Box>
              <Paper sx={{ p: 2, borderRadius: 0, boxShadow: 'none' }}>
                <Skeleton width={200} height={24} />
                <Grid container spacing={2} mt={1}>
                  {[1, 2, 3, 4].map((i) => (
                    <Grid item xs={6} sm={3} key={i}>
                      <Skeleton height={28} />
                      <Skeleton height={14} width="80%" />
                    </Grid>
                  ))}
                </Grid>
              </Paper>

              <Paper sx={{ p: 2, mb: 2, borderRadius: 0, boxShadow: 'none' }}>
                <Skeleton width={180} height={22} />
                <Stack spacing={1.5} mt={2}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} height={44} />
                  ))}
                </Stack>
              </Paper>

              <Paper sx={{ p: 2, borderRadius: 0, boxShadow: 'none' }}>
                <Skeleton width={150} height={22} />
                <Stack spacing={1} mt={2}>
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} height={48} />
                  ))}
                </Stack>
              </Paper>
            </Box>
          ) : (
            /* =================== REAL CONTENT =================== */
            <>
              <Box
                sx={{
                  backgroundColor: isDarkMode ? "#1E1E1E" : "#ffffff",
                  // borderRadius: 2,
                  mb: 0,

                }}
              >
                {/* ================= Configuration Changes ================= */}
                <Box sx={{ ...rowStyle, pl: 2 }}>
                  <Typography sx={labelStyle}>
                    Configuration Changes :
                  </Typography>

                  <Box sx={{ display: "flex", fontSize: "18px", fontWeight: 700 }}>
                    <InlineStatBlock
                      label="All"
                      value={configurationChanges.all_changes}
                    />

                    {!isLinuxVendor && (
                      <>
                        <InlineStatBlock
                          label="Addition"
                          value={configurationChanges.additions}
                        />
                        <InlineStatBlock
                          label="Deletion"
                          value={configurationChanges.deletions}
                        />
                        <InlineStatBlock
                          label="Modified"
                          value={configurationChanges.modified}
                        />
                      </>
                    )}
                  </Box>

                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* ================= Vulnerability Changes ================= */}
                <Box sx={{ ...rowStyle, mb: 1, pl: 2 }}>
                  <Typography sx={labelStyle}>
                    Vulnerabiity Changes :
                  </Typography>

                  <Box sx={{ display: "flex", fontSize: "18px", fontWeight: 700, }}>
                    <InlineStatBlock
                      label="Total Changes"
                      value={vulnerabilityChanges.total_changes}
                      reverseArrows={true}
                    />
                    <InlineStatBlock
                      label="New Misconfigs"
                      value={vulnerabilityChanges.added}
                      reverseArrows={true}
                    />
                    <InlineStatBlock
                      label="Remediated"
                      value={vulnerabilityChanges.removed}
                      reverseArrows={true}
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* ================= CVE Changes ================= */}
                <Box sx={{ ...rowStyle, pl: 2 }}>
                  <Typography sx={labelStyle}>
                    CVE Changes :
                  </Typography>

                  <Box sx={{ display: "flex", fontSize: "18px", fontWeight: 700 }}>
                    <InlineStatBlock
                      label="Total"
                      value={cveChanges.total_count}
                      reverseColors={true}
                    />
                    <InlineStatBlock
                      label="Added"
                      value={cveChanges.added}
                      reverseColors={true}
                    />
                    <InlineStatBlock
                      label="Remediated"
                      value={-Math.abs(Number(cveChanges.removed))}
                      reverseColors={true}
                    />
                  </Box>
                </Box>
                <Divider  />

              </Box>
            </>
          )}
        </div>

        {/* Misconfiguration Changes Section */}
        <Box
          sx={{
            pl: 2,
            pr: 2,
            backgroundColor: isDarkMode ? "#1E1E1E" : "#ffffff",
            // borderRadius:2,
            boxShadow: 'none',
          }}
        >
          <Accordion
            defaultExpanded
            disableGutters
            square
            sx={{
              borderRadius: 2,
              // clip child backgrounds to maintain rounded corners
              overflow: "hidden",
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 2,
                bgcolor: isDarkMode ? "#1E1E1E" : "#E2E2F2",
                py: 1.5,
                "& .MuiAccordionSummary-content": {
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                },
                // Ensure expand icon wrapper remains transparent/square
                "& .MuiAccordionSummary-expandIconWrapper": {
                  bgcolor: "transparent",
                  borderRadius: 0,
                },
                // round only the top corners of the summary so it matches Accordion
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "13px" }}>
                Misconfiguration Changes
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <FormControl
                  size="small"
                  onClick={handleFilterInteraction}
                  onFocus={handleFilterInteraction}
                  onKeyDown={handleFilterInteraction}
                >
                  <Select
                    value={misconfigFilter}
                    onChange={(event) =>
                      setMisconfigFilter(event.target.value as "all" | "changes")
                    }
                    onClick={handleFilterInteraction}
                    onClose={handleFilterInteraction}
                    onOpen={handleFilterInteraction}
                    onKeyDown={handleFilterInteraction}
                    sx={{
                      fontSize: "0.8rem",
                      "& .MuiSelect-select": {
                        py: 0.75,
                      },
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="changes">Changes</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </AccordionSummary>
            <Divider />
            <Box
              sx={{
                px: 2,
                py: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: "12px" }}>
                Parameter Names
              </Typography>
            </Box>
            <Divider />
            <AccordionDetails >
              {isLoading ? (
                <Stack spacing={1.5}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        py: 1,
                        gap: 1.5,
                      }}
                    >
                      <Skeleton variant="rectangular" width={4} height={40} sx={{ borderRadius: 999 }} />
                      <Skeleton variant="text" sx={{ flex: 1, fontSize: "14px" }} />
                      <Skeleton variant="rounded" width={32} height={32} />
                    </Box>
                  ))}
                </Stack>
              ) : filteredMisconfigurationChanges.length ? (
                <Box
                  sx={{
                    minHeight: Math.min(
                      filteredMisconfigurationChanges.length * 54,
                      110
                    ),
                    maxHeight: Math.min(
                      filteredMisconfigurationChanges.length * 44,
                      200
                    ),
                    overflowY: "auto",
                  }}
                  className="scroll"
                >
                  <Stack spacing={1.5}>
                    {filteredMisconfigurationChanges.map((item: any) => {
                      const meta = MISCONFIG_STATUS_META[item.status as keyof typeof MISCONFIG_STATUS_META];
                      const StatusIcon = meta.icon;
                      return (
                        <Box
                          key={item.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            py: .5,
                            gap: 1.5,
                          }}
                        >
                          {/* Left status bar */}
                          <Box
                            sx={{
                              width: 4,
                              borderRadius: 999,
                              alignSelf: "stretch",
                              backgroundColor: meta.color,
                            }}
                          />
                          

                          {/* Parameter Name */}
                          <WhiteTooltip title={item.name}>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ fontWeight: 600, flex: 1, cursor: "default" }}
                            >
                              {item.name}
                            </Typography>
                          </WhiteTooltip>

                          {/* Status Icon */}
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              position: "relative",
                              backgroundColor: meta.iconBg,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "4px",
                            }}
                          >
                            {item.showNotApplicable ? (
                              <Box
                                component="img"
                                src={NotApplicableImg}
                                alt="Not applicable"
                                sx={{ width: 18, height: 18 }}
                              />
                            ) : (
                              <StatusIcon sx={{ fontSize: 18, color: meta.color }} />
                            )}

                            {/* 🔴 Red dot */}
                            {item.showRedDot && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  right: 0,
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  backgroundColor: "#F03C3C",
                                }}
                              />
                            )}

                            {/* 🟢 Green dot */}
                            {!item.showRedDot && item.showGreenDot && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  right: 0,
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  backgroundColor: "#00A61B",
                                }}
                              />
                            )}

                            {/* ⚪ Gray dot for previous NOT_APPLICABLE */}
                            {item.showGrayDot && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  right: 0,
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  backgroundColor: isDarkMode ? "#9CA3AF" : "#9EA7AD",
                                  border: `1px solid ${theme.palette.background.paper}`,
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {misconfigFilter === "all"
                    ? "No misconfigurations available."
                    : "No misconfiguration changes available."}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* CVE Changes Section */}
        <Box
          sx={{
            pl: 2,
            pr: 2,
            backgroundColor: isDarkMode ? "#1E1E1E" : "#ffffff",
            boxShadow: 'none',
          }}
        >
          <Accordion
            defaultExpanded
            disableGutters
            square
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              // clip child backgrounds to maintain rounded corners
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 2,
                bgcolor: isDarkMode ? "#1E1E1E" : "#E2E2F2",
                py: 1.5,
                "& .MuiAccordionSummary-content": {
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                },
                "& .MuiAccordionSummary-expandIconWrapper": {
                  bgcolor: "transparent",
                  borderRadius: 0,
                },
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                CVE's Changes
              </Typography>
            </AccordionSummary>
            <Divider />
            <AccordionDetails sx={{ px: 0, py: 0 }}>
              {isLoading ? (
                <Box sx={{ px: 2, py: 2 }}>
                  <Stack spacing={1}>
                    <Skeleton variant="rectangular" height={40} />
                    {[1, 2, 3, 4].map((i) => (
                      <Skeleton key={i} variant="rectangular" height={48} />
                    ))}
                  </Stack>
                </Box>
              ) : hasCveChanges ? (
                <AgGridTable
                  columnDefs={cveColumnDefs}
                  data={filteredCves}
                  showSearch={false}
                  showAppsIcon={false}
                  pagination
                  height={Math.min(summaryData.cves.length * 60 + 80, 280)}
                  gridOptions={{
                    rowStyle: {
                      background: "white",
                      cursor: "default",
                    },
                    getRowStyle: (params) => ({
                      background:
                        params.node?.rowIndex! % 2 === 0
                          ? "#FFFFFF"
                          : "#F9FAFB",
                    }),
                  }}
                />
              ) : (
                <Box sx={{ px: 2, py: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", fontWeight: 600 }}
                  >
                    No CVE changes available.
                  </Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      </Stack>
    </Drawer>
  );
};

export default ViewSummaryDrawer;